import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  ISingleAttendanceException,
  IToggleCompanyEmployeeAttendanceRequest,
  IUpdateAttendanceExceptionStatusRequest,
  TGetCompanyEmployeeCodesListResponse,
  TGetCompanyEmployeeTimeAttendanceSummaryRequest,
  TGetCompanyEmployeeTimeAttendanceSummaryResponse,
  TGetCompanyTimeAttendanceStatsRequest,
  TGetCompanyTimeAttendanceStatsResponse,
  TGetCompanyTimeAttendanceSummaryRequest,
  TGetCompanyTimeAttendanceSummaryResponse,
  TTimeAttendanceActivityResponse,
  TTimeAttendanceOverviewResponse,
} from "@/types/(company)/timeAttendance.interface";
import type { IAttendanceDayDetailResponse } from "@/types/(my-space)/timeAttendace.interface";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";

export interface IAttendanceExceptionStats {
  total: number;
  pending: number;
  approved: number;
  autoApproved: number;
  rejected: number;
  cancelled: number;
}

export const companyTimeAttendanceApi = createApi({
  reducerPath: "companyTimeAttendanceApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getTimeAttendanceOverview",
    "getTimeAttendanceActivity",
    "getCompanyTimeAttendanceSummary",
    "getCompanyTimeAttendanceStats",
    "getCompanyEmployeeCodesFilterList",
    "getCompanyEmployeeTimeAttendanceSummary",
    "getAttendanceExceptions",
    "getAttendanceExceptionStats",
  ],
  endpoints: (builder) => ({
    getTimeAttendanceOverview: builder.query<
      TTimeAttendanceOverviewResponse,
      { bucket: "year" | "month" | "week" }
    >({
      query: ({ bucket }) => ({
        url: "/company/time-attendance/overview",
        params: { bucket: bucket },
      }),
      providesTags: ["getTimeAttendanceOverview"],
    }),
    getTimeAttendanceActivity: builder.query<
      TTimeAttendanceActivityResponse,
      { limit?: number }
    >({
      query: ({ limit }) => ({
        url: "/company/time-attendance/activity",
        params: { limit },
      }),
      providesTags: ["getTimeAttendanceActivity"],
    }),
    getCompanyTimeAttendanceSummary: builder.query<
      TGetCompanyTimeAttendanceSummaryResponse,
      TGetCompanyTimeAttendanceSummaryRequest
    >({
      query: (params) => {
        return {
          url: "/company/time-attendance/employees",
          ...(params && Object.keys(params).length > 0 ? { params } : {}),
        };
      },
      providesTags: ["getCompanyTimeAttendanceSummary"],
    }),
    getCompanyTimeAttendanceStats: builder.query<
      TGetCompanyTimeAttendanceStatsResponse,
      TGetCompanyTimeAttendanceStatsRequest
    >({
      query: (params) => {
        const query = {
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.date ? { date: params.date } : {}),
        };
        return {
          url: "/company/time-attendance/stats",
          ...(Object.keys(query).length ? { params: query } : {}),
        };
      },
      providesTags: ["getCompanyTimeAttendanceStats"],
    }),
    toggleCompanyEmployeeAttendance: builder.mutation<
      any,
      { id: string; body: IToggleCompanyEmployeeAttendanceRequest }
    >({
      query: ({ id, body }) => {
        return {
          url: `/company/time-attendance/toggle-clock/${id}`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: [
        "getCompanyEmployeeTimeAttendanceSummary",
        "getCompanyTimeAttendanceSummary",
        "getCompanyTimeAttendanceStats",
      ],
    }),
    getCompanyEmployeeTimeAttendanceSummary: builder.query<
      TGetCompanyEmployeeTimeAttendanceSummaryResponse,
      TGetCompanyEmployeeTimeAttendanceSummaryRequest
    >({
      query: (params) => {
        return {
          url: "/company/time-attendance/summary",
          ...(params && Object.keys(params).length > 0 ? { params } : {}),
        };
      },
      providesTags: ["getCompanyEmployeeTimeAttendanceSummary"],
    }),
    getCompanyEmployeeDayDetail: builder.query<
      IAttendanceDayDetailResponse,
      { userId: number; date: string }
    >({
      query: (params) => ({
        url: "/company/time-attendance/drawer",
        params,
      }),
      // Share the summary tag so regularize/clock mutations refetch the drawer.
      providesTags: ["getCompanyEmployeeTimeAttendanceSummary"],
    }),
    getCompanyEmployeeCodesFilterList: builder.query<
      TGetCompanyEmployeeCodesListResponse,
      void
    >({
      query: () => {
        return {
          url: "/company/time-attendance/emplaoyeeCodes",
        };
      },
      providesTags: ["getCompanyEmployeeCodesFilterList"],
    }),
    deleteCompanyEmployeeAttendanceItem: builder.mutation<
      any,
      { id: string; userId: string }
    >({
      query: ({ id, userId }) => {
        return {
          url: `/company/time-attendance/employees/${id}?userId=${userId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["getCompanyEmployeeTimeAttendanceSummary"],
    }),
    //Attendance Exceptions APIs
    getAllAttendanceExceptions: builder.query<
      IApiPaginatedResponse<ISingleAttendanceException[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: APIEndpoints.Company.TimeAttendance.AttendanceExceptions
          .GetAllAttendanceExceptions,
        params,
      }),
      providesTags: ["getAttendanceExceptions"],
    }),
    getAttendanceExceptionStats: builder.query<
      IApiResponse<IAttendanceExceptionStats>,
      void
    >({
      query: () => ({
        url: "/company/attendance-exceptions/stats",
        method: "GET",
      }),
      providesTags: ["getAttendanceExceptionStats"],
    }),
    getAttendanceExceptionById: builder.query<
      IApiPaginatedResponse<ISingleAttendanceException>,
      number
    >({
      query: (id) => ({
        url: APIEndpoints.Company.TimeAttendance.AttendanceExceptions.GetAttendanceExceptionById(
          id,
        ),
      }),
    }),
    updateAttendanceExceptionStatus: builder.mutation<
      IApiPaginatedResponse<void>,
      { id: number; body: IUpdateAttendanceExceptionStatusRequest }
    >({
      query: ({ id, body }) => ({
        url: APIEndpoints.Company.TimeAttendance.AttendanceExceptions.UpdateAttendanceExceptionStatus(
          id,
        ),
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getAttendanceExceptions", "getAttendanceExceptionStats"],
    }),
    updateCompanyQuickActivityLog: builder.mutation<
      IApiResponse<unknown>,
      {
        id: number;
        body: {
          quickActivityStartTime?: string;
          quickActivityEndTime?: string;
        };
      }
    >({
      query: ({ id, body }) => ({
        url: APIEndpoints.Company.TimeAttendance.UpdateQuickActivityLog(id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getCompanyEmployeeTimeAttendanceSummary"],
    }),
  }),
});

export const {
  useGetTimeAttendanceOverviewQuery,
  useGetTimeAttendanceActivityQuery,
  useGetCompanyTimeAttendanceSummaryQuery,
  useGetCompanyTimeAttendanceStatsQuery,
  useToggleCompanyEmployeeAttendanceMutation,
  useGetCompanyEmployeeTimeAttendanceSummaryQuery,
  useGetCompanyEmployeeDayDetailQuery,
  useGetCompanyEmployeeCodesFilterListQuery,
  useDeleteCompanyEmployeeAttendanceItemMutation,
  useGetAllAttendanceExceptionsQuery,
  useGetAttendanceExceptionStatsQuery,
  useGetAttendanceExceptionByIdQuery,
  useUpdateAttendanceExceptionStatusMutation,
  useUpdateCompanyQuickActivityLogMutation,
} = companyTimeAttendanceApi;
